import React, { useEffect, useState } from 'react';
import { ref, onValue, query, orderByChild, equalTo } from 'firebase/database';
import { database } from '../firebaseConfig';

// Messages (emails + texts) Resi sent for one record, from crm/messageLog.
//   field: 'contactId' | 'transactionId' | 'pipelineId', value: the record id.
// The database only returns entries the viewer may see (see database.rules.json crm/messageLog).
export const TYPE_LABEL = { blast: 'Email blast', template: 'Template email', app_email: 'Email', automated_deal: 'Automated deal email', welcome: 'Welcome email', quo_text: 'Text (Quo)' };
export const fmtEt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' }) + ' ET' : '');
const listOf = (v) => Object.entries(v || {}).map(([id, r]) => ({ id, ...r })).sort((a, b) => String(b.sentAt).localeCompare(String(a.sentAt)));

export const useMessages = (field, value) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!value) { setRows([]); return undefined; }
    return onValue(query(ref(database, 'crm/messageLog'), orderByChild(field), equalTo(value)), (s) => setRows(listOf(s.val())), () => setRows([]));
  }, [field, value]);
  return rows;
};

export function MessageRow({ m, showAgent = false }) {
  const [open, setOpen] = useState(false);
  const td = { padding: '6px 8px', borderBottom: '1px solid #eee', verticalAlign: 'top', fontSize: 13 };
  const color = m.status === 'sent' ? '#2e7d32' : m.status === 'failed' ? '#b00020' : '#8C5A00';
  return (
    <>
      <tr onClick={() => setOpen(!open)} style={{ cursor: 'pointer' }}>
        <td style={td}>{fmtEt(m.sentAt)}</td>
        <td style={td}>{m.channel === 'text' ? 'Text' : 'Email'} · {TYPE_LABEL[m.type] || m.type}</td>
        {showAgent && <td style={td}>{m.agentName || '—'}</td>}
        <td style={td}>{(m.to || []).join(', ')}</td>
        <td style={td}>{m.channel === 'text' ? (m.bodyPreview || '').slice(0, 60) : m.subject}</td>
        <td style={{ ...td, color, fontWeight: 700 }}>{m.status}{m.testMode ? ' (test)' : ''}</td>
      </tr>
      {open && (
        <tr><td colSpan={showAgent ? 6 : 5} style={{ ...td, background: '#FBF7EC' }}>
          <div><strong>From:</strong> {m.fromName ? `${m.fromName} <${m.from}>` : m.from}</div>
          {(m.replyTo || []).length > 0 && <div><strong>Reply-to:</strong> {m.replyTo.join(', ')}</div>}
          {(m.cc || []).length > 0 && <div><strong>Cc:</strong> {m.cc.join(', ')}</div>}
          <div><strong>To:</strong> {(m.to || []).join(', ')}</div>
          {m.subject && <div><strong>Subject:</strong> {m.subject}</div>}
          {m.bodyPreview && <div><strong>Message:</strong> {m.bodyPreview}</div>}
          <div><strong>Sent by:</strong> {m.agentName || '—'}</div>
          {m.error && <div style={{ color: '#b00020' }}><strong>Error:</strong> {m.error}</div>}
          {m.fallbackReason && <div><strong>Why Firebase email:</strong> {m.fallbackReason}</div>}
          <div style={{ color: '#666' }}>{[m.contactId && `Contact ${m.contactId}`, m.transactionId && `Deal ${m.transactionId}`, m.pipelineId && `Pipeline ${m.pipelineId}`, m.blastId && `Blast ${m.blastId}`].filter(Boolean).join(' · ')}</div>
        </td></tr>
      )}
    </>
  );
}

export function MessagesTable({ rows, showAgent = false, empty = 'No messages logged yet.' }) {
  if (rows === null) return <p style={{ fontSize: 13 }}>Loading…</p>;
  if (!rows.length) return <p style={{ fontSize: 13, color: '#666' }}>{empty}</p>;
  const th = { textAlign: 'left', fontSize: 12, padding: '6px 8px', borderBottom: '2px solid #d8c9a8' };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={th}>Date</th><th style={th}>Type</th>{showAgent && <th style={th}>Agent</th>}<th style={th}>To</th><th style={th}>Subject / message</th><th style={th}>Status</th></tr></thead>
        <tbody>{rows.map((m) => <MessageRow key={m.id} m={m} showAgent={showAgent} />)}</tbody>
      </table>
    </div>
  );
}

// "Messages" section for a deal, pipeline record or contact. Click a row for details.
export default function MessagesSection({ field, value, title = 'Messages' }) {
  const rows = useMessages(field, value);
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: '1px solid #e5e5e5', borderRadius: 8, padding: 12, margin: '12px 0', background: '#fff' }}>
      <button type="button" onClick={() => setOpen(!open)} style={{ background: 'none', border: 0, fontWeight: 700, fontSize: 15, cursor: 'pointer', padding: 0 }}>
        {open ? '▾' : '▸'} {title} {rows ? `(${rows.length})` : ''}
      </button>
      {open && <div style={{ marginTop: 8 }}><MessagesTable rows={rows} /></div>}
    </div>
  );
}
